/**
 * @file Frontend utility — adminFormFieldValidation.
 * Maps admin form UI state ↔ API validation fields for TEXT fields
 * (POST/PATCH /forms/admin/field).
 */

export type TextValidationFormSlice = {
  defaultValue: string;
  textMaxLength: string;
  numberMin: string;
  numberMax: string;
  maxDecimalPlaces: string;
};

/** Exported function — text Validation From Field. */
export function textValidationFromField(field: {
  defaultValue?: string | null;
  textMaxLength?: number | null;
  numberMin?: string | number | null;
  numberMax?: string | number | null;
  maxDecimalPlaces?: number | null;
}): TextValidationFormSlice {
  return {
    defaultValue:
      field.defaultValue != null && field.defaultValue !== ""
        ? String(field.defaultValue)
        : "",
    textMaxLength:
      field.textMaxLength != null ? String(field.textMaxLength) : "",
    numberMin:
      field.numberMin != null && field.numberMin !== ""
        ? String(field.numberMin)
        : "",
    numberMax:
      field.numberMax != null && field.numberMax !== ""
        ? String(field.numberMax)
        : "",
    maxDecimalPlaces:
      field.maxDecimalPlaces != null ? String(field.maxDecimalPlaces) : "",
  };
}

function parseIntOrNull(s: string): number | null {
  const t = String(s ?? "").trim();
  if (t === "") return null;
  const n = parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
}

function parseNumOrNull(s: string): number | null {
  const t = String(s ?? "").trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Mutates payload with TEXT validation keys when type is TEXT. */
export function appendTextValidationPayload(
  payload: Record<string, unknown>,
  type: string,
  slice: TextValidationFormSlice
): void {
  if (type !== "TEXT") return;
  payload.defaultValue = slice.defaultValue.trim();
  payload.textMaxLength = parseIntOrNull(slice.textMaxLength);
  payload.numberMin = parseNumOrNull(slice.numberMin);
  payload.numberMax = parseNumOrNull(slice.numberMax);
  payload.maxDecimalPlaces = parseIntOrNull(slice.maxDecimalPlaces);
}

/** Type definition — Text Field Constraint Form. */
export type TextFieldConstraintForm = {
  type: string;
  inputType: string;
  textMaxLength: string;
  numberMin: string;
  numberMax: string;
};

/**
 * Enforces: text / default → max length required; number (integer) / double → min & max required.
 * Returns an error message or null if valid.
 */
export function validateTextFieldInputConstraints(
  form: TextFieldConstraintForm
): string | null {
  if (form.type !== "TEXT") return null;
  const it = String(form.inputType ?? "").trim();

  const isTextMode = it === "" || it === "text";
  const isNumericMode =
    it === "number" || it === "double" || it === "integer";

  if (isTextMode) {
    const raw = String(form.textMaxLength ?? "").trim();
    if (raw === "") {
      return "Max length is required when input type is text.";
    }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0) {
      return "Max length must be a non-negative whole number.";
    }
    return null;
  }

  if (isNumericMode) {
    const a = String(form.numberMin ?? "").trim();
    const b = String(form.numberMax ?? "").trim();
    if (a === "" || b === "") {
      return "Minimum and maximum are required when input type is integer or decimal.";
    }
    const min = Number(a);
    const max = Number(b);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return "Minimum and maximum must be valid numbers.";
    }
    if (min > max) {
      return "Minimum cannot be greater than maximum.";
    }
    return null;
  }

  return null;
}
